
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Input,
  Textarea,
  Select,
  FormLabel,
  FormControl,
  FormErrorMessage,
  Text,
  SimpleGrid,
  Spinner,
  useToast,
  Badge,
  HStack,
  Switch,
  FormHelperText,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { motion } from "framer-motion";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";


const ServiceTypeForm = () => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [translationKey, setTranslationKey] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [status, setStatus] = useState("on");
  const [translations, setTranslations] = useState({ fr: "", it: "" });
  const [image, setImage] = useState("");
  const [serviceCategories, setServiceCategories] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [imageSize, setImageSize] = useState(null);
  const imageInputRef = useRef(null);
  const toast = useToast();
  const pageId = 13;

  // Helper for image size validation (max 1MB)
  const validateImageSize = (file) => file.size <= 1024 * 1024;

  // Convert the selected file to a base64 string with size check
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageSize(file.size);
      if (!validateImageSize(file)) {
        setFieldErrors((prev) => ({ ...prev, image: t("Image must be less than 1MB") }));
        return;
      }
      setFieldErrors((prev) => ({ ...prev, image: undefined }));
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setImage(reader.result);
      };
    } else {
      setImageSize(null);
    }
  };

  // Fetch available service categories from API when the component mounts
  useEffect(() => {
    const fetchServiceCategories = async () => {
      try {
        const data = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/service-categories?page_id=${pageId}`,
          "GET"
        );
        setServiceCategories(data.data);
      } catch (error) {
        toast({
          title: t("Error fetching service categories"),
          description: error.message || t("Failed to load service categories."),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    };
    fetchServiceCategories();
  }, [t, toast]);

  const handleTranslationChange = (lang, value) => {
    setTranslations({ ...translations, [lang]: value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    const data = {
      name,
      description,
      translation_key: translationKey,
      service_category_id: serviceId,
      status,
      translations,
      ...(image ? { image } : {}),
    };

    try {
      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/service-types?page_id=${pageId}`,
        "POST",
        data
      );

      setName("");
      setDescription("");
      setTranslationKey("");
      setServiceId("");
      setStatus("on");
      setTranslations({ fr: "", it: "" });
      setImage("");
      setError("");
      setSuccess(true);

      // Reset the file input element
      if (imageInputRef.current) imageInputRef.current.value = "";

      setTimeout(() => setSuccess(false), 2000);
    } catch (error) {
      setError(error.message || t("An error occurred. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5 }}
    >
      <Card p={{ base: "20px", md: "30px" }}>
        <form onSubmit={handleSubmit}>
          {error && (
            <Text color="#FF3B3B" textAlign="center" fontSize="lg">
              {error}
            </Text>
          )}
          {success && (
            <Text color="green.500" textAlign="center">
              {t("Service Type added successfully!")}
            </Text>
          )}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={6}>
            {/* Name */}
            <FormControl isRequired isInvalid={!!fieldErrors.name}>
              <FormLabel color="blue.600">{t("Service Type Name")}</FormLabel>
              <Input
                placeholder={t("Service Type Name")}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <FormErrorMessage>{fieldErrors.name}</FormErrorMessage>
            </FormControl>
            {/* Description */}
            <FormControl isRequired isInvalid={!!fieldErrors.description}>
              <FormLabel color="blue.600">{t("Description")}</FormLabel>
              <Textarea
                placeholder={t("Description")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                minH="40px"
                size="sm"
                borderRadius={"8px"}
              />
              <FormErrorMessage>{fieldErrors.description}</FormErrorMessage>
            </FormControl>
            {/* Translation Key */}
            <FormControl isRequired isInvalid={!!fieldErrors.translationKey}>
              <FormLabel color="blue.600">{t("Translation Key")}</FormLabel>
              <Input
                placeholder={t("Translation Key")}
                type="text"
                value={translationKey}
                onChange={(e) => setTranslationKey(e.target.value)}
                required
              />
              <FormErrorMessage>{fieldErrors.translationKey}</FormErrorMessage>
            </FormControl>
            {/* Translation (French) */}
            <FormControl>
             <FormLabel color="blue.600">{t("Translation ")} 
                <Text 
                //bold text
                as="span" fontWeight="bold"
                >
                  {t("(French)")} 
                </Text>
              </FormLabel>              <Input
                placeholder={t("Translation (French)")}
                type="text"
                value={translations.fr}
                onChange={(e) => handleTranslationChange("fr", e.target.value)}
              />
            </FormControl>
            {/* Translation (Italy) */}
            <FormControl>
              <FormLabel color="blue.600">{t("Translation ")} 
                <Text 
                //bold text
                as="span" fontWeight="bold"
                >
                  {t("(Italy)")} 
                </Text>
              </FormLabel>
              <Input
                placeholder={t("Translation (Italy)")}
                type="text"
                value={translations.it}
                onChange={(e) => handleTranslationChange("it", e.target.value)}
              />
            </FormControl>
           
           
                 {/* Service Category */}
            <FormControl isRequired isInvalid={!!fieldErrors.serviceId}>
              <FormLabel color="blue.600">{t("Select Service Category")}</FormLabel>
              <Select
                placeholder={t("Select Service Category")}
                value={serviceId}
                onChange={(e) => setServiceId(e.target.value)}
                required
              >
                {serviceCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{fieldErrors.serviceId}</FormErrorMessage>
            </FormControl>
                {/* Image Upload */}
      <FormControl isInvalid={!!fieldErrors.image}>
  <FormLabel color="blue.600">{t("Image")}</FormLabel>

  <Input
    type="file"
    accept="image/*"
    onChange={handleImageUpload}
    ref={imageInputRef}
  />

  {/* Always show the helper text prominently */}
  <Text fontSize="sm" color="gray.500" mt={2}>
    {t("Recommended size: Max 1 MB (≈1024 KB), up to 1024×1024 pixels")}
  </Text>

  {/* Show file size information */}
  {imageSize !== null && (
    <Text
      fontSize="xs"
      color={imageSize > 1024 * 1024 ? "red.500" : "gray.500"}
      mt={1}
    >
      {t("Selected file size")}: {(imageSize / 1024).toFixed(1)} KB
      {imageSize > 1024 * 1024 && ` (${t("Too large")})`}
    </Text>
  )}

  {/* Show error message if there is an error */}
  <FormErrorMessage>{fieldErrors.image}</FormErrorMessage>
</FormControl>
           {/* Status */}
            <FormControl isRequired>
                        <FormLabel color="blue.600">{t("Status")}</FormLabel>
                      
            <HStack spacing={4}>
              {/* Switch for toggling status */}
              <Switch
                isChecked={status === "on"}
                onChange={(e) => setStatus(e.target.checked ? "on" : "off")}
                colorScheme="cyan"
              />
              {/* Badge to display current status */}
              <Badge
                colorScheme={status === "on" ? "green" : "red"}
                variant="solid"
                px={3}
                py={1}
                borderRadius="md"
              >
                {status === "on" ? t("Active") : t("Inactive")}
              </Badge>
            </HStack>
          </FormControl>
          </SimpleGrid>
          <Box>
            <Button
              mt={4}
              colorScheme="cyan"
              fontSize="m"
              borderRadius="16px"
              w={{ base: "100%", md: "148px" }}
              h="46px"
              type="submit"
              color="white"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner size="sm" /> : t("Submit")}
            </Button>
          </Box>
          {/* Show selected image with label at the bottom */}
          <Box mt={8}>
            <Text fontWeight="bold" mb={2}>{t("Selected Images")}</Text>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={6}>
              {image && (
                <Box textAlign="center">
                  <Text fontSize="sm" mb={1}>{t("Image")}</Text>
                  <img src={image} alt="Image Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                </Box>
              )}
            </SimpleGrid>
          </Box>
        </form>
      </Card>
    </motion.div>
  );
};

export default ServiceTypeForm;
